import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Plus, Users, UserX, Baby, School, GraduationCap as GradCap } from "lucide-react";
import { AdminPageScaffold } from "@/components/core/AdminPageScaffold";
import { DataViewScaffold } from "@/components/core/DataViewScaffold";
import { StandardPageHeader } from "@/components/layout/StandardPageHeader";
import { SearchInput } from "@/components/ui/SearchInput";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
// import { useStudentsData } from "@/hooks/useStudentsData";
import { useOptimizedStudentsData } from "@/hooks/useOptimizedStudentsData"; // Now using optimized version for better performance
import { useStudentActions } from "@/hooks/useStudentActions";
import { useSearch } from "@/hooks/useSearch";
import { usePerformanceTimer } from "@/hooks/usePerformanceTimer";
import { useHivePackTerminology } from "@/hooks/useHivePackCustomization";
import { useAuth } from "@/hooks/useAuth";
import { Student } from "@/types/models";
import StudentCard from "@/components/students/StudentCard";
import StudentTableRow from "@/components/students/StudentTableRow";
import AddStudentDialog from "@/components/students/AddStudentDialog";
import EditStudentDialog from "@/components/students/EditStudentDialog";

type FilterType = "all" | "enrolled" | "not-enrolled" | "young" | "teen" | "adult" | "with-family" | "without-family";

const Students = () => {
  const navigate = useNavigate();
  const { organizationId } = useAuth();
  const terminology = useHivePackTerminology(organizationId);
  
  // Search
  const { searchQuery, debouncedSearchQuery, setSearchQuery, clearSearch, hasSearch } = useSearch();
  
  // State
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [isAddStudentDialogOpen, setIsAddStudentDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Data - Now using optimized hook for better performance
  const { students, families, enrollmentCounts, loading, setStudents } = useOptimizedStudentsData();
  const { isLoading: isActionLoading, handleStudentAdded } = useStudentActions();
  
  // Performance monitoring
  usePerformanceTimer('Students Page Data Loading', loading);

  const getStudentAge = (student: Student) => {
    if (!student.date_of_birth) return null;
    const birthDate = new Date(student.date_of_birth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };


  // Apply filters with memoization for performance
  const filteredStudents = useMemo(() => {
    let filtered = students;

    // Apply search filter using debounced query
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase().trim();
      filtered = filtered.filter(student => {
        const fullName = `${student.first_name || ''} ${student.last_name || ''}`.toLowerCase();
        const familyName = families[student.family_id]?.toLowerCase() || '';
        
        return fullName.includes(query) || familyName.includes(query);
      });
    }

    // Apply type filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(student => {
        switch (activeFilter) {
          case 'enrolled':
            return (enrollmentCounts[student.id] || 0) > 0;
          case 'not-enrolled':
            return (enrollmentCounts[student.id] || 0) === 0;
          case 'young':
            const youngAge = getStudentAge(student);
            return youngAge !== null && youngAge >= 3 && youngAge <= 10;
          case 'teen':
            const teenAge = getStudentAge(student);
            return teenAge !== null && teenAge >= 11 && teenAge <= 17;
          case 'adult':
            const adultAge = getStudentAge(student);
            return adultAge !== null && adultAge >= 18;
          case 'with-family':
            return Boolean(families[student.family_id]);
          case 'without-family':
            return !families[student.family_id];
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [students, families, enrollmentCounts, debouncedSearchQuery, activeFilter]);

  // Event handlers
  const handleAddStudent = () => {
    setIsAddStudentDialogOpen(true);
  };

  const onStudentAdded = async (studentData: any) => {
    try {
      const newStudent = await handleStudentAdded(studentData);
      setStudents(prev => [...prev, newStudent]);
      setIsAddStudentDialogOpen(false);
    } catch (error) {
      console.error("Error adding student:", error);
    }
  };

  const handleEdit = useCallback((student: Student) => {
    setSelectedStudent(student);
    setIsEditDialogOpen(true);
  }, []);

  const handleViewFamily = useCallback((student: Student) => {
    if (families[student.family_id]) {
      navigate(`/admin/families/${student.family_id}`);
    }
  }, [families, navigate]);

  const handleViewEnrollments = useCallback((student: Student) => {
    navigate(`/admin/students/${student.id}`);
  }, [navigate]);

  const handleViewStudent = useCallback((student: Student) => {
    navigate(`/admin/students/${student.id}`);
  }, [navigate]);

  const handleAssignToFamily = useCallback((student: Student) => {
    // TODO: Implement family assignment dialog
    console.log('Assign to family:', student);
  }, []);

  const handleEnrollInClass = useCallback((student: Student) => {
    // TODO: Navigate to class enrollment
    navigate(`/admin/students/${student.id}?tab=enrollments`);
  }, [navigate]);

  // Stable render functions
  const renderStudentCard = useCallback((student: Student) => {
    return (
      <StudentCard
        key={student.id}
        student={student}
        familyName={families[student.family_id]}
        enrollmentCount={enrollmentCounts[student.id] || 0}
        onClick={() => handleViewStudent(student)}
      />
    );
  }, [families, enrollmentCounts, handleViewStudent]);

  const renderStudentTableRow = useCallback((student: Student) => {
    return (
      <StudentTableRow
        key={student.id}
        student={student}
        familyName={families[student.family_id]}
        enrollmentCount={enrollmentCounts[student.id] || 0}
        onClick={() => handleViewStudent(student)}
        onEdit={handleEdit}
        onViewFamily={handleViewFamily}
        onViewEnrollments={handleViewEnrollments}
        onAssignToFamily={handleAssignToFamily}
        onEnrollInClass={handleEnrollInClass}
      />
    );
  }, [families, enrollmentCounts, handleViewStudent, handleEdit, handleViewFamily, handleViewEnrollments, handleAssignToFamily, handleEnrollInClass]);

  const renderEmptyState = () => {
    if (hasSearch || activeFilter !== "all") {
      const getFilterEmptyMessage = () => {
        switch (activeFilter) {
          case "enrolled":
            return `No ${terminology.students} are currently enrolled in ${terminology.classes}`;
          case "not-enrolled":
            return `All ${terminology.students} are enrolled! 🎉`;
          case "without-family":
            return `All ${terminology.students} have been assigned to families! ✨`;
          case "young":
            return "No kids (ages 3-10) found";
          case "teen":
            return "No teens (ages 11-17) found";
          case "adult":
            return "No adults (ages 18+) found";
          default:
            return hasSearch 
              ? `No ${terminology.students} found matching "${searchQuery}"`
              : `No ${terminology.students} match your current filters`;
        }
      };

      const getFilterSubMessage = () => {
        switch (activeFilter) {
          case "not-enrolled":
            return `Every ${terminology.student} is actively participating in ${terminology.classes}`;
          case "without-family":
            return `Great work organizing your ${terminology.student} families`;
          default:
            return hasSearch 
              ? "Try adjusting your search terms"
              : "Try selecting a different filter or clearing your current selection";
        }
      };

      return (
        <div className="text-center py-16">
          <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center mb-6">
            <GraduationCap className="h-8 w-8 text-blue-500" />
          </div>
          <h3 className="text-xl font-medium mb-2">{getFilterEmptyMessage()}</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            {getFilterSubMessage()}
          </p>
          <div className="flex gap-3 justify-center">
            {hasSearch && (
              <Button variant="outline" onClick={clearSearch}>
                Clear Search
              </Button>
            )}
            {activeFilter !== "all" && (
              <Button variant="outline" onClick={() => setActiveFilter("all")}>
                View All {terminology.students.charAt(0).toUpperCase() + terminology.students.slice(1)}
              </Button>
            )}
            <Button onClick={handleAddStudent}>
              <Plus className="h-4 w-4 mr-2" />
              Add {terminology.student.charAt(0).toUpperCase() + terminology.student.slice(1)}
            </Button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="text-center py-20">
        <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center mb-8">
          <GraduationCap className="h-10 w-10 text-purple-600" />
        </div>
        <h3 className="text-2xl font-semibold mb-3">Welcome to {terminology.student.charAt(0).toUpperCase() + terminology.student.slice(1)} Management</h3>
        <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
          Get started by adding your first {terminology.student}. You can assign them to families, 
          track their enrollments, and manage all their information in one place.
        </p>
        <Button size="lg" onClick={handleAddStudent} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-5 w-5 mr-2" />
          Add Your First {terminology.student.charAt(0).toUpperCase() + terminology.student.slice(1)}
        </Button>
      </div>
    );
  };

  const renderFilterBar = () => {
    const filterOptions = [
      { key: "all", label: `All ${terminology.students.charAt(0).toUpperCase() + terminology.students.slice(1)}`, icon: Users, count: students.length },
      { key: "enrolled", label: "Enrolled", icon: GradCap, count: students.filter(s => (enrollmentCounts[s.id] || 0) > 0).length },
      { key: "not-enrolled", label: "Not Enrolled", icon: UserX, count: students.filter(s => (enrollmentCounts[s.id] || 0) === 0).length },
      { key: "without-family", label: "Missing Family", icon: UserX, count: students.filter(s => !families[s.family_id]).length },
      { key: "young", label: "Kids (3-10)", icon: Baby, count: students.filter(s => { const age = getStudentAge(s); return age !== null && age >= 3 && age <= 10; }).length },
      { key: "teen", label: "Teens (11-17)", icon: School, count: students.filter(s => { const age = getStudentAge(s); return age !== null && age >= 11 && age <= 17; }).length },
      { key: "adult", label: "Adults (18+)", icon: GraduationCap, count: students.filter(s => { const age = getStudentAge(s); return age !== null && age >= 18; }).length },
    ];

    return (
      <div className="flex flex-wrap gap-2 mb-6">
        {filterOptions.map((option) => {
          const Icon = option.icon;
          const isActive = activeFilter === option.key;
          const needsAttention = option.key === "not-enrolled" || option.key === "without-family";
          
          return (
            <Button
              key={option.key}
              variant={isActive ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveFilter(option.key as FilterType)}
              className={`flex items-center gap-2 transition-all duration-200 ${
                isActive ? "shadow-sm" : "hover:shadow-sm"
              } ${needsAttention && option.count > 0 && !isActive ? "border-orange-200 text-orange-700 hover:bg-orange-50" : ""}`}
            >
              <Icon className="h-4 w-4" />
              <span>{option.label}</span>
              {option.count > 0 && (
                <Badge 
                  variant={isActive ? "secondary" : "outline"} 
                  className={`ml-1 text-xs ${needsAttention && !isActive ? "bg-orange-100 text-orange-700 border-orange-200" : ""}`}
                >
                  {option.count}
                </Badge>
              )}
            </Button>
          );
        })}
      </div>
    );
  };

  const statsInfo = useMemo(() => {
    const totalStudents = students.length;
    const enrolledCount = students.filter(s => (enrollmentCounts[s.id] || 0) > 0).length;
    const enrollmentRate = totalStudents > 0 ? Math.round((enrolledCount / totalStudents) * 100) : 0;
    
    return { totalStudents, enrolledCount, enrollmentRate };
  }, [students, enrollmentCounts]);

  return (
    <>
      <StandardPageHeader
        title={`${terminology.student.charAt(0).toUpperCase() + terminology.student.slice(1)} Management`}
        subtitle={`${statsInfo.totalStudents} ${terminology.students} • ${statsInfo.enrolledCount} enrolled (${statsInfo.enrollmentRate}%)`}
        icon={GraduationCap}
        actions={
          <Button onClick={handleAddStudent} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Student
          </Button>
        }
      />
      
      <AdminPageScaffold
        loading={loading}
        error={null}
        isEmpty={students.length === 0 && activeFilter === "all"}
        emptyState={renderEmptyState()}
        enableMotion={false}
      >
        {/* FILTER BAR */}
        {renderFilterBar()}

        {/* DATA VIEW */}
        <DataViewScaffold
          items={filteredStudents}
          views={['card', 'table']}
          defaultView="card"
          renderCard={renderStudentCard}
          renderTableRow={renderStudentTableRow}
          renderTableHeader={() => (
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Student</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Family</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Age</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Enrollments</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          )}
          emptyState={renderEmptyState()}
          storageKey="students-view-preference"
          cardColumns={{ sm: 1, md: 2, lg: 3, xl: 4 }}
        />
      </AdminPageScaffold>

      {/* Dialogs - Always rendered outside AdminPageScaffold */}
      <AddStudentDialog
        open={isAddStudentDialogOpen}
        onOpenChange={setIsAddStudentDialogOpen}
        onStudentAdded={onStudentAdded}
        isLoading={isActionLoading}
      />
      
      {selectedStudent && (
        <EditStudentDialog
          student={selectedStudent}
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          onStudentUpdated={() => {
            // Refresh data
            setIsEditDialogOpen(false);
            setSelectedStudent(null);
          }}
        />
      )}
    </>
  );
};

export default Students;
